import { useMemo } from "react";

import { useEvent } from "../../../changelog-item-context";

import { Permissions } from "@/providers/RbacProvider/constants";
import { useRbac } from "@/providers/RbacProvider";
import { useGetWorkspaceMemberRoles } from "@/queries/workspace";
import { useUser } from "@/context/user-context";
import { ActionType, EntityType } from "@/queries/folder-entities/types";
import { usePromptLabelConfiguration, useWorkflowLabelConfiguration } from "@/queries/labels";
import { useToolLabelConfiguration } from "@/queries/tool-registry";

/**
 * Hook to determine if the current user can approve protected label changes.
 *
 * Users who can approve must meet ONE of these criteria:
 * 1. Workspace Admins: Users with the Permissions.ADMIN permission
 * 2. Approver Role Members: Users assigned to the specific approver role configured on the protected label
 */
export const useCanApprove = () => {
  const event = useEvent();
  const { permissions } = useRbac();
  const { user, activeWorkspaceId, workspaceMembers } = useUser();

  // Find the current user's workspace member record
  // The API expects WorkspaceMember.id, not User.id
  const currentWorkspaceMember = useMemo(
    () => workspaceMembers?.find(member => member.user_id === user?.id),
    [workspaceMembers, user?.id],
  );
  const workspaceMemberId = currentWorkspaceMember?.id;

  // Get user's roles in the workspace
  const { data: memberRolesData } = useGetWorkspaceMemberRoles(
    activeWorkspaceId ?? 0,
    workspaceMemberId ?? 0,
    !!(activeWorkspaceId && workspaceMemberId),
  );

  const userRoleIds = useMemo(
    () => memberRolesData?.roles?.map(role => role.id) ?? [],
    [memberRolesData?.roles],
  );

  // Check if user is workspace admin
  const isWorkspaceAdmin = permissions?.includes(Permissions.ADMIN) ?? false;

  // Extract label information from the event
  const { entity_type, action_payload, action_type } = event;

  // Only check for label-related actions
  const isLabelAction = [ActionType.MOVE_LABEL, ActionType.DELETE_LABEL].includes(action_type);

  // Get label ID from action payload
  const labelId = useMemo<number | null>(() => {
    if (!action_payload || !isLabelAction) {
      return null;
    }

    if ("prompt_label_id" in action_payload && typeof action_payload.prompt_label_id === "number") {
      return action_payload.prompt_label_id;
    }
    if (
      "workflow_label_id" in action_payload &&
      typeof action_payload.workflow_label_id === "number"
    ) {
      return action_payload.workflow_label_id;
    }
    if ("tool_label_id" in action_payload && typeof action_payload.tool_label_id === "number") {
      return action_payload.tool_label_id;
    }
    return null;
  }, [action_payload, isLabelAction]);

  // Fetch label configuration based on entity type
  const isPrompt = entity_type === EntityType.PROMPT;
  const isWorkflow = entity_type === EntityType.WORKFLOW;
  const isTool = entity_type === EntityType.TOOL;

  const { data: promptLabelConfig } = usePromptLabelConfiguration(
    isPrompt ? labelId : null,
    isPrompt && isLabelAction,
  );

  const { data: workflowLabelConfig } = useWorkflowLabelConfiguration(
    isWorkflow ? labelId : null,
    isWorkflow && isLabelAction,
  );

  const { data: toolLabelConfig } = useToolLabelConfiguration(
    isTool ? labelId : null,
    isTool && isLabelAction,
  );

  // Get the appropriate label configuration
  const getLabelConfig = () => {
    if (isPrompt) {
      return promptLabelConfig;
    }
    if (isWorkflow) {
      return workflowLabelConfig;
    }
    if (isTool) {
      return toolLabelConfig;
    }
    return undefined;
  };
  const labelConfig = getLabelConfig();

  // Determine if user can approve
  const canApprove = useMemo(() => {
    if (!isLabelAction || !labelConfig) {
      // If not a label action or couldn't fetch label config, deny approval
      return false;
    }

    if (!labelConfig.is_protected) {
      // If label is not protected, no approval needed (everyone can approve)
      return true;
    }

    // Check if user is workspace admin
    if (isWorkspaceAdmin) {
      return true;
    }

    // Check if user has the specific approver role
    if (labelConfig.approver_role_id) {
      return userRoleIds.includes(labelConfig.approver_role_id);
    }

    return true;
  }, [isLabelAction, labelConfig, isWorkspaceAdmin, userRoleIds]);

  return {
    canApprove,
    isLoading:
      !memberRolesData ||
      (!promptLabelConfig && !workflowLabelConfig && !toolLabelConfig && isLabelAction),
  };
};
